import { useSettings, type ConnectionView } from "@helm/core";
import { formatCents, formatMoney, pnlTone } from "@helm/ui";
import {
  headlineOf,
  sameBrokerRow,
  useAccountView,
  useTradingViewSlice,
  type TradingView,
} from "../viewmodel/account";
import { RoomGauge } from "./RoomGauge";
import { RiskCoverage } from "../risk/RiskCoverage";
import { BrokerRiskFigures } from "../risk/BrokerRiskFigures";

/** The account the per-account surfaces act on (selected, or main while All is selected). */
const activeRow = (v: TradingView) => v.accounts.find((a) => a.summary.id === v.activeId) ?? null;

/**
 * Mobile Home (SPEC §17.7; Tradesea Bal / RP&L / UP&L). Figures are `$ --` until accounts exist.
 * The connection and session state is the shell's `ConnectionBanner` (CB5); while the gateway is
 * not connected account P&L is unavailable.
 */
export function Home({ connection }: { connection: ConnectionView }): React.JSX.Element {
  const connected = connection.status === "connected";
  const view = useAccountView();
  const privacy = useSettings((s) => s.settings.privacy);
  const money = (v: number | null, signed = true): string => formatMoney(v, { privacy, signed });
  // "All accounts" (chosen on desktop): P&L figures are totals; room and limits stay per account.
  const head = headlineOf(view);
  // ADR-0045/0047: the active account's room figures — Rithmic's, else HELM-calculated from the
  // owner's firm rules (re-rendered with its account row).
  const row = useTradingViewSlice(activeRow, sameBrokerRow);
  const dd = row?.broker.trailingDrawdown ?? null;
  return (
    <div className="m-home">
      <div className="m-card m-pnl">
        <p className="muted">Rithmic currency: {head.currency ?? "unavailable"}</p>
        <div className="m-pnl-main">
          <span className="kpi-label">
            Unrealized P&amp;L
            {!connected && (
              <span className="m-stale" data-testid="pnl-stale">
                {" "}
                · unavailable while disconnected
              </span>
            )}
          </span>
          <span className={`m-pnl-value num tone-${pnlTone(head.unrealizedPnl)}`}>
            {money(head.unrealizedPnl)}
          </span>
        </div>
        <div className="kpi-row">
          <div className="kpi">
            <span className="kpi-label">RP&amp;L</span>
            <span className={`kpi-value num tone-${pnlTone(head.realizedPnl)}`}>
              {money(head.realizedPnl)}
            </span>
          </div>
          <div className="kpi">
            <span className="kpi-label">Rithmic balance</span>
            <span className="kpi-value num">{money(head.balance, false)}</span>
          </div>
          <div className="kpi">
            <span className="kpi-label">Day</span>
            <span className={`kpi-value num tone-${pnlTone(head.dayNetPnl)}`}>{money(head.dayNetPnl)}</span>
          </div>
        </div>
      </div>
      <div className="m-card">
        <RiskCoverage connected={connected} />
        <RoomGauge
          fraction={null}
          status="none"
          label={`DD room · ${dd?.source === "helm_calculated" ? "TradePrizm-calculated" : (dd?.verification ?? "unavailable")}`}
          value={dd?.valueCents == null ? "—" : formatCents(dd.valueCents, { privacy })}
        />
      </div>
      <div className="m-card m-limits">
        {row ? <BrokerRiskFigures row={row} /> : <p>No Rithmic account.</p>}
      </div>
      <div className="m-spark" aria-label="Today's P&L sparkline">
        <span className="muted">P&amp;L sparkline appears once fills exist.</span>
      </div>
      <button
        type="button"
        className="btn btn-kill"
        disabled
        title="Kills every strategy instance (SPEC §11A.10)"
      >
        Kill all strategies
      </button>
    </div>
  );
}
